if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('.product-media-modal__content');
        this.nav = this.querySelector('.product-media-modal__nav');
        this.thumbnailButtons = Array.from(this.querySelectorAll('[data-modal-media-id]'));
        this.mobileHintDots = Array.from(this.querySelectorAll('[data-modal-hint-media-id]'));
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onModalTouchStart = this.onModalTouchStart.bind(this);
        this.onModalTouchMove = this.onModalTouchMove.bind(this);
        this.onModalTouchEnd = this.onModalTouchEnd.bind(this);
        this.onModalScroll = this.onModalScroll.bind(this);
        this.stopModalClose = this.stopModalClose.bind(this);
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchStartScale = 1;
        this.touchStartedWithMultipleFingers = false;
        this.touchHadMultipleFingers = false;
        this.scrollSyncTimeout = null;

        if (this.nav) {
          this.nav.addEventListener('pointerdown', this.stopModalClose);
          this.nav.addEventListener('pointerup', this.stopModalClose);
          this.nav.addEventListener('click', this.stopModalClose);
        }

        this.thumbnailButtons.forEach((button) => {
          button.addEventListener('pointerdown', this.stopModalClose);
          button.addEventListener('pointerup', this.stopModalClose);
          button.addEventListener('click', this.onThumbnailClick);
        });

        if (this.content) {
          this.content.addEventListener('pointerdown', this.stopModalClose);
          this.content.addEventListener('pointerup', this.stopModalClose);
          this.content.addEventListener('click', this.stopModalClose);
          this.content.addEventListener('touchstart', this.onModalTouchStart, { passive: true });
          this.content.addEventListener('touchmove', this.onModalTouchMove, { passive: true });
          this.content.addEventListener('touchend', this.onModalTouchEnd, { passive: true });
          this.content.addEventListener('scroll', this.onModalScroll, { passive: true });
        }
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      stopModalClose(event) {
        event.stopPropagation();
      }

      onModalTouchStart(event) {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartScale = window.visualViewport ? window.visualViewport.scale : 1;
        this.touchStartedWithMultipleFingers = event.touches.length > 1;
        this.touchHadMultipleFingers = event.touches.length > 1;
      }

      onModalTouchMove(event) {
        if (event.touches.length > 1) {
          this.touchHadMultipleFingers = true;
        }
      }

      onModalTouchEnd(event) {
        if (window.innerWidth >= 750) return;
        if (this.touchStartedWithMultipleFingers || this.touchHadMultipleFingers) {
          if (event.touches.length === 0) {
            this.touchStartedWithMultipleFingers = false;
            this.touchHadMultipleFingers = false;
          }
          return;
        }

        const currentScale = window.visualViewport ? window.visualViewport.scale : 1;
        const isZoomed = currentScale > 1.02 || this.touchStartScale > 1.02;
        if (isZoomed) return;

        this.syncActiveMediaFromScroll();
      }

      onModalScroll() {
        if (window.innerWidth >= 750) return;

        window.clearTimeout(this.scrollSyncTimeout);
        this.scrollSyncTimeout = window.setTimeout(() => this.syncActiveMediaFromScroll(), 80);
      }

      onThumbnailClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const mediaId = event.currentTarget.dataset.modalMediaId;
        if (!mediaId) return;

        const targetMedia = this.findContentMedia(mediaId);
        if (!targetMedia) return;

        this.updateActiveThumbnail(mediaId);
        if (this.content) {
          if (window.innerWidth < 750) {
            const scrollTarget =
              targetMedia.getBoundingClientRect().left - this.content.getBoundingClientRect().left + this.content.scrollLeft;
            this.content.scrollTo({ left: Math.max(scrollTarget, 0), top: 0, behavior: 'smooth' });
          } else {
            const scrollTarget =
              targetMedia.getBoundingClientRect().top - this.content.getBoundingClientRect().top + this.content.scrollTop - 12;
            this.content.scrollTo({ top: Math.max(scrollTarget, 0), behavior: 'smooth' });
          }
        } else {
          targetMedia.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }

        const targetMediaTemplate = targetMedia.querySelector('template');
        const targetMediaContent = targetMediaTemplate ? targetMediaTemplate.content : null;

        if (
          targetMedia.nodeName == 'DEFERRED-MEDIA' &&
          targetMediaContent &&
          targetMediaContent.querySelector('.js-youtube')
        ) {
          targetMedia.loadContent();
        }
      }

      getOpenerMediaId(opener) {
        if (!opener) return null;

        if (opener.getAttribute && opener.getAttribute('data-media-id')) {
          return opener.getAttribute('data-media-id');
        }

        const closestMedia = opener.closest ? opener.closest('[data-media-id]') : null;
        if (closestMedia) return closestMedia.getAttribute('data-media-id');

        const nestedMedia = opener.querySelector ? opener.querySelector('[data-media-id]') : null;
        return nestedMedia ? nestedMedia.getAttribute('data-media-id') : null;
      }

      findContentMedia(mediaId) {
        if (!this.content || !mediaId) return null;

        return Array.from(this.content.querySelectorAll('[data-media-id]')).find(
          (element) => element.getAttribute('data-media-id') === mediaId
        );
      }

      getContentMediaItems() {
        return this.content ? Array.from(this.content.querySelectorAll('[data-media-id]')) : [];
      }

      showAdjacentMedia(direction) {
        const contentMediaItems = this.getContentMediaItems();
        if (contentMediaItems.length < 2) return;

        const activeIndex = contentMediaItems.findIndex((element) => element.classList.contains('active'));
        const nextIndex =
          activeIndex >= 0
            ? (activeIndex + direction + contentMediaItems.length) % contentMediaItems.length
            : 0;

        this.activateMedia(contentMediaItems[nextIndex], 'smooth');
      }

      syncActiveMediaFromScroll() {
        if (!this.content) return;

        const contentMediaItems = this.getContentMediaItems();
        if (!contentMediaItems.length) return;

        const contentRect = this.content.getBoundingClientRect();
        const centerX = contentRect.left + contentRect.width / 2;
        const activeMedia = contentMediaItems
          .map((element) => {
            const rect = element.getBoundingClientRect();
            return {
              element,
              distance: Math.abs(rect.left + rect.width / 2 - centerX),
            };
          })
          .sort((a, b) => a.distance - b.distance)[0]?.element;

        if (!activeMedia) return;

        contentMediaItems.forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });
        this.updateActiveThumbnail(activeMedia.getAttribute('data-media-id'));
      }

      getCenteredGalleryMediaId() {
        const slides = Array.from(document.querySelectorAll('.product__media-wrapper .product__media-item'));
        if (!slides.length) return null;

        const viewportCenter = window.innerWidth / 2;
        const centeredSlide = slides
          .map((slide) => {
            const rect = slide.getBoundingClientRect();
            const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));

            return {
              slide,
              visibleWidth,
              distance: Math.abs(rect.left + rect.width / 2 - viewportCenter),
            };
          })
          .filter((item) => item.visibleWidth > 0)
          .sort((a, b) => b.visibleWidth - a.visibleWidth || a.distance - b.distance)[0];

        if (!centeredSlide) return null;

        const mediaElement = centeredSlide.slide.querySelector('[data-media-id]');
        return mediaElement ? mediaElement.getAttribute('data-media-id') : null;
      }

      getRequestedMediaId() {
        const openerMediaId = this.getOpenerMediaId(this.openedBy);
        const centeredMediaId = this.getCenteredGalleryMediaId();

        if (!centeredMediaId || window.innerWidth >= 750) return openerMediaId || centeredMediaId;
        if (!openerMediaId) return centeredMediaId;

        const openerSlide = this.openedBy.closest ? this.openedBy.closest('.product__media-item') : null;
        if (!openerSlide) return centeredMediaId;

        const rect = openerSlide.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const mostlyVisible = visibleWidth >= rect.width * 0.55;

        return mostlyVisible ? openerMediaId : centeredMediaId;
      }

      updateActiveThumbnail(mediaId) {
        this.thumbnailButtons.forEach((button) => {
          const isActive = button.dataset.modalMediaId === mediaId;
          button.classList.toggle('is-active', isActive);
          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        this.mobileHintDots.forEach((dot) => {
          dot.classList.toggle('is-active', dot.dataset.modalHintMediaId === mediaId);
        });
      }

      activateMedia(activeMedia, behavior = 'auto') {
        const contentMediaItems = this.getContentMediaItems();
        if (!activeMedia) return;

        contentMediaItems.forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        const activeMediaId = activeMedia.getAttribute('data-media-id');
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        this.updateActiveThumbnail(activeMediaId);

        const container = this.querySelector('[role="document"]');
        if (this.content) {
          if (window.innerWidth < 750) {
            const scrollTarget =
              activeMedia.getBoundingClientRect().left - this.content.getBoundingClientRect().left + this.content.scrollLeft;
            this.content.scrollTo({ left: Math.max(scrollTarget, 0), top: 0, behavior });
          } else {
            const scrollTarget =
              activeMedia.getBoundingClientRect().top - this.content.getBoundingClientRect().top + this.content.scrollTop - 12;
            this.content.scrollTo({ top: Math.max(scrollTarget, 0), left: 0, behavior });
          }
        } else {
          activeMedia.scrollIntoView({ behavior });
        }
        if (container && activeMedia.width) {
          container.scrollLeft = Math.max((activeMedia.width - container.clientWidth) / 2, 0);
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      showActiveMedia() {
        const mediaId = this.getRequestedMediaId();
        const contentMediaItems = this.getContentMediaItems();
        const activeMedia = this.findContentMedia(mediaId) || contentMediaItems[0];

        if (!activeMedia) return;
        this.activateMedia(activeMedia);
      }
    }
  );
}
